import React, { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowUpRight, Loader2, Smartphone, X } from "lucide-react";
import { createIntouchPayDeposit } from "../../api/intouchPayApi";
import {
  getApiErrorMessage,
  getPaymentProviderMessage,
  getAccountPhoneInputValue,
  isPhoneOnlyAccount,
  isPaymentDepositSuccess,
  isValidRwandanPhone,
  normalizePhoneInput,
  parsePaymentAmount,
  PAYMENT_METHODS,
  resolvePaymentUserId,
} from "../../utils/paymentHelpers";
import "./payments.css";

const DepositModal = ({ open, onClose, userId, user, onSuccess, onNotify }) => {
  const titleId = useId();
  const [amount, setAmount] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("MTN");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isPhoneLocked = isPhoneOnlyAccount(user);

  const resetForm = () => {
    setAmount("");
    setPhoneNumber("");
    setPaymentMethod("MTN");
    setError("");
  };

  useEffect(() => {
    if (!open) return undefined;

    resetForm();
    if (isPhoneOnlyAccount(user) && user?.phoneNumber) {
      setPhoneNumber(getAccountPhoneInputValue(user.phoneNumber));
    }

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !loading) onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const handleClose = () => {
    if (loading) return;
    resetForm();
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const numAmount = parsePaymentAmount(amount);
    if (!numAmount) {
      setError("Enter a valid amount greater than 0.");
      return;
    }

    const normalizedPhone = normalizePhoneInput(
      isPhoneLocked ? getAccountPhoneInputValue(user?.phoneNumber) : phoneNumber
    );
    if (!normalizedPhone) {
      setError("Enter your mobile money phone number.");
      return;
    }

    if (!isValidRwandanPhone(normalizedPhone)) {
      setError("Enter a valid Rwandan phone number (e.g. 0781234567).");
      return;
    }

    const resolvedUserId = resolvePaymentUserId(userId);
    if (!resolvedUserId) {
      setError("Sign in again to deposit.");
      return;
    }

    try {
      setLoading(true);
      const data = await createIntouchPayDeposit({
        userId: resolvedUserId,
        amount: numAmount,
        phoneNumber: normalizedPhone,
        paymentMethod,
      });

      if (!isPaymentDepositSuccess(data)) {
        const message =
          getPaymentProviderMessage(data) || "Deposit could not be started.";
        setError(message);
        onNotify?.("error", "Deposit failed", message);
        return;
      }

      const providerMessage = getPaymentProviderMessage(data);

      onNotify?.(
        "success",
        "Deposit initiated",
        providerMessage ||
          "Confirm the payment on your phone. MTN: dial *182*7*1# if you do not see a prompt."
      );

      onSuccess?.(data);
      handleClose();
    } catch (err) {
      const message = getApiErrorMessage(err, "Unable to initiate deposit");
      setError(message);
      onNotify?.("error", "Deposit failed", message);
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="pay-modal-root" role="presentation">
      <button
        type="button"
        className="pay-modal-backdrop"
        aria-label="Close dialog"
        disabled={loading}
        onClick={handleClose}
      />

      <form
        className="pay-modal-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={handleSubmit}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="pay-modal-head">
          <span className="pay-modal-head-icon" aria-hidden>
            <ArrowUpRight size={16} />
          </span>
          <div className="pay-modal-head-copy">
            <h2 id={titleId}>Deposit to BetBond</h2>
            <p>Deposit via mobile money</p>
          </div>
          <button
            type="button"
            className="pay-modal-close"
            aria-label="Close"
            disabled={loading}
            onClick={handleClose}
          >
            <X size={14} />
          </button>
        </header>

        <div className="pay-modal-body">
          <p className="pay-modal-hint">
            Funds are added to your Cash balance after you approve the prompt on your phone.
          </p>

          <div className="pay-modal-field">
            <label className="pay-modal-label" htmlFor="pay-deposit-amount">
              Amount (Cash)
            </label>
            <div className="pay-modal-input-wrap">
              <span className="pay-modal-prefix">Cash</span>
              <input
                id="pay-deposit-amount"
                type="number"
                min="1"
                step="1"
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="Enter amount"
                disabled={loading}
              />
            </div>
          </div>

          <div className="pay-modal-field">
            <label className="pay-modal-label" htmlFor="pay-deposit-phone">
              Phone number
            </label>
            {isPhoneLocked ? (
              <p className="pay-modal-hint pay-modal-hint-inline">
                Your account phone number is used for mobile money.
              </p>
            ) : null}
            <div className="pay-modal-input-wrap">
              <Smartphone size={14} aria-hidden />
              <input
                id="pay-deposit-phone"
                type="tel"
                inputMode="tel"
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value)}
                placeholder="0781234567"
                disabled={loading}
                readOnly={isPhoneLocked}
                className={isPhoneLocked ? "pay-modal-input-locked" : undefined}
              />
            </div>
          </div>

          <div className="pay-modal-field">
            <span className="pay-modal-label">Payment method</span>
            <div className="pay-modal-methods" role="radiogroup" aria-label="Payment method">
              {PAYMENT_METHODS.map((method) => (
                <button
                  key={method.id}
                  type="button"
                  className={`pay-modal-method${
                    paymentMethod === method.id ? " pay-modal-method-active" : ""
                  }`}
                  onClick={() => setPaymentMethod(method.id)}
                  disabled={loading}
                  aria-pressed={paymentMethod === method.id}
                >
                  {method.label}
                </button>
              ))}
            </div>
          </div>

          {error ? <p className="pay-modal-error">{error}</p> : null}
        </div>

        <div className="pay-modal-foot">
          <button type="submit" className="pay-modal-submit" disabled={loading}>
            {loading ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Processing…
              </>
            ) : (
              "Deposit"
            )}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default DepositModal;
